import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fingerprint } from 'lucide-react';
import { ApiFailure, authApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { AuthShell, AuthSeparator } from '@/components/ui/AuthShell';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Banner';
import { loginWithPasskey } from '@/pages/SecurityPage';

export function LoginPage() {
  const navigate = useNavigate();
  const setChallenge = useAuthStore((s) => s.setChallenge);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!email || !password) {
      setError('Email et mot de passe requis');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authApi.login(email.trim().toLowerCase(), password);
      setChallenge(res.challengeToken, res.mfaSetupRequired);
      navigate(res.mfaSetupRequired ? '/mfa/setup' : '/mfa/verify');
    } catch (err) {
      setError(err instanceof ApiFailure ? mapError(err) : 'Erreur réseau - merci de réessayer');
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePasskeyLogin(): Promise<void> {
    if (passkeyLoading) return;
    setError(null);
    setPasskeyLoading(true);
    try {
      const res = await loginWithPasskey();
      if (!res) {
        // user a annulé le prompt navigateur
        return;
      }
      // La passkey suffit (auth + facteur en une seule étape).
      // On créé directement la session, pas de challenge MFA intermédiaire.
      setAuthenticated({
        accessToken: res.accessToken,
        user: res.user as Parameters<typeof setAuthenticated>[0]['user'],
      });
      navigate('/');
    } catch (err) {
      setError(
        err instanceof ApiFailure ? mapError(err) : 'Échec passkey - utilisez votre mot de passe.',
      );
    } finally {
      setPasskeyLoading(false);
    }
  }

  return (
    <AuthShell title="Connexion" subtitle="Accédez à votre espace de gestion commerciale.">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <Label htmlFor="email" required>
            Adresse email
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="password" required>
            Mot de passe
          </Label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error && <Banner tone="danger">{error}</Banner>}

        <Button type="submit" variant="primary" className="w-full" loading={submitting}>
          {submitting ? 'Connexion…' : 'Se connecter'}
        </Button>
      </form>

      <AuthSeparator />

      <Button
        type="button"
        variant="secondary"
        className="w-full"
        leadingIcon={<Fingerprint className="h-4 w-4 text-accent" />}
        onClick={() => void handlePasskeyLogin()}
        loading={passkeyLoading}
      >
        Se connecter avec une passkey
      </Button>

      <Button
        type="button"
        variant="secondary"
        className="mt-2 w-full"
        leadingIcon={<MicrosoftLogo />}
        onClick={() => setError('Connexion Microsoft : configuration MSAL à finaliser.')}
      >
        Se connecter avec Microsoft
      </Button>

      <p className="mt-8 text-center text-xs text-text-muted">
        Mot de passe oublié ? Contactez votre administrateur.
      </p>
    </AuthShell>
  );
}

function mapError(err: ApiFailure): string {
  if (err.code === 'UNAUTHORIZED') return 'Identifiants invalides ou compte bloqué.';
  if (err.code === 'RATE_LIMITED') return 'Trop de tentatives. Réessayez dans une minute.';
  if (err.code === 'VALIDATION_ERROR') return 'Email ou mot de passe invalide.';
  return err.message;
}

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
      <rect x="0" y="0" width="7" height="7" fill="#F35325" />
      <rect x="9" y="0" width="7" height="7" fill="#81BC06" />
      <rect x="0" y="9" width="7" height="7" fill="#05A6F0" />
      <rect x="9" y="9" width="7" height="7" fill="#FFBA08" />
    </svg>
  );
}
